import { StyleSheet, View, Platform, Image } from "react-native";
import { Video } from "expo-av";
import { memo, useEffect, useRef, useState } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { API_BASE_URL } from "../config";

const BackgroundVideoBanner = memo(({ onVideoLoaded, refreshTrigger }) => {
  const videoRef = useRef(null);
  const hasNotified = useRef(false);
  const [mobBanner, setMobBanner] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleVideoLoaded = () => {
    if (!hasNotified.current && onVideoLoaded) {
      hasNotified.current = true;
      onVideoLoaded();
    }
  };

  const handleImageLoaded = () => {
    setIsLoading(false);
    handleVideoLoaded();
  };

  const handleImageError = () => {
    console.warn("Error loading mobBanner image, falling back to video");
    setMobBanner(null);
    setIsLoading(false);
  };

  const loadMobBanner = async () => {
    try {
      setIsLoading(true);
      const banner = await AsyncStorage.getItem('mobBanner');
      if (banner) {
        const fullBannerUrl = API_BASE_URL + "/" + banner;
        console.log("Full constructed mobBanner:", fullBannerUrl);
        setMobBanner(fullBannerUrl);
      } else {
        setMobBanner(null);
      }
      setIsLoading(false);
    } catch (error) {
      console.warn("Error loading mobBanner from AsyncStorage:", error);
      setMobBanner(null);
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadMobBanner();

    return () => {
      hasNotified.current = false;
      if (videoRef.current?.unloadAsync) {
        try {
          videoRef.current.unloadAsync();
        } catch (error) {
          console.warn("Error cleaning up video:", error);
        }
      }
    };
  }, []);

  // Re-load when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger) {
      console.log("Refreshing background banner due to trigger change");
      loadMobBanner();
    }
  }, [refreshTrigger]);

  useEffect(() => {
    // Setup video only when mobBanner is null and not loading
    if (!mobBanner && !isLoading && Platform.OS !== "web") {
      const setupVideo = async () => {
        try {
          await videoRef.current?.setStatusAsync({
            isLooping: true,
            isMuted: true,
            shouldPlay: true,
            volume: 0,
          });
          handleVideoLoaded();
        } catch (error) {
          console.warn("Error setting up video:", error);
          handleVideoLoaded();
        }
      };

      setupVideo();
    }
  }, [mobBanner, isLoading]);

  // Show loading state while checking for mobBanner
  if (isLoading) {
    return <View style={[styles.videoContainer, styles.loadingContainer]} />;
  }

  return (
    <View style={styles.videoContainer}>
      {mobBanner ? (
        // Render image if mobBanner exists
        Platform.OS === "web" ? (
          <img
            src={mobBanner}
            alt="Mobile Banner"
            style={styles.video}
            onLoad={handleImageLoaded}
            onError={handleImageError}
          />
        ) : (
          <Image
            source={{ uri: mobBanner }}
            style={styles.video}
            resizeMode="cover"
            onLoad={handleImageLoaded}
            onError={handleImageError}
          />
        )
      ) : (
        // Render video if no mobBanner
        Platform.OS === "web" ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            onCanPlay={handleVideoLoaded}
            onError={(e) => {
              console.warn("Video loading error (web):", e);
              handleVideoLoaded();
            }}
            style={styles.video}
          >
            <source src="/assets/vid/banner3.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <Video
            ref={videoRef}
            source={require("../assets/vid/banner3.mp4")}
            resizeMode="cover"
            shouldPlay
            isLooping
            isMuted
            style={styles.video}
            onLoad={handleVideoLoaded}
            onError={(error) => {
              console.warn("Video loading error (native):", error);
              handleVideoLoaded();
            }}
          />
        )
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  videoContainer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: -1,
    backgroundColor: "#0d2b6a",
  },
  video: {
    flex: 1,
    width: "100%",
    height: "100%",
    objectFit: "cover", // required for HTML video element to behave like resizeMode: 'cover'
  },
  loadingContainer: {
    backgroundColor: "#0d2b6a",
  },
});

export default BackgroundVideoBanner;